import { styled } from "@mui/material/styles";
import ContextConstructor from "csi/def/Context";
import html from "csi/html";
import { getCommentC8InstanceCnl } from "csi/model/comment";
import { isUnreadNotification, updateNotifications } from "csi/service/notificationService";
import { useNotificationStore } from "csi/service/NotificationStore";
import sessionConstants from "csi/sessionConstants";
import { routerStore } from "csi/state/rootStore";
import { C8Node } from "csi/typings";
import { UserAvatar } from "csi/user/UserAvatar";
import xml from "csi/xml";
import { MessageDialog, OverflowText, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { black, blue, grey, white } from "designSystem/styles/color";
import { throttle } from "lodash";
import React, { FunctionComponent, useRef } from "react";
import csiCard from "src/csi/card/_base";
import csiHeading from "src/csi/heading/_base";
import types from "src/csi/types";
import ReadUnreadBtn from "./ReadUnreadBtn";

const PREFIX = "ChatCard";

const classes = {
	chatCard: `${PREFIX}chatCard`,
	chatCardNew: `${PREFIX}chatCardNew`,
	image40: `${PREFIX}image40`,
	infoPane: `${PREFIX}infoPane`,
	dateAssigned: `${PREFIX}dateAssigned`,
	secondRow: `${PREFIX}secondRow`,
	mentioned: `${PREFIX}mentioned`,
	thirdRow: `${PREFIX}thirdRow`,
	comment: `${PREFIX}comment`,
	mentionTag: `${PREFIX}mentionTag`,
	fourthRow: `${PREFIX}fourthRow`,
	separator: `${PREFIX}separator`,
};

const Root = styled("div")((
	{
		theme,
	},
) => ({
	[`&.${classes.chatCard}`]: {
		height: 103,
		width: 496,
		padding: 12,
		borderBottom: "1px solid rgba(0, 0, 0, .05)",
		boxSizing: "border-box",
		display: "flex",
		flexDirection: "row",
		background: white[50],
		position: "relative",
		"&:hover": {
			background: grey[200],
		},
		"&:active": {
			background: grey[300],
		},
	},

	[`&.${classes.chatCardNew}`]: {
		background: blue[50],
	},

	[`& .${classes.image40}`]: {
		maxHeight: 40,
		maxWidth: 40,
		height: 40,
		width: 40,
		marginRight: 12,
		display: "flex",
		alignItems: "center",
	},

	[`& .${classes.infoPane}`]: {
		display: "flex",
		flexDirection: "column",
		width: 395,
		"& span": {
			minHeight: 18,
		},
	},

	[`& .${classes.dateAssigned}`]: {
		fontSize: 10,
		verticalAlign: "middle",
		color: grey[800],
		padding: "0 12px",
		fontWeight: 600,
	},

	[`& .${classes.secondRow}`]: {
		marginTop: 6,
		display: "flex",
	},

	[`& .${classes.mentioned}`]: {
		color: black[100],
		fontSize: 12,
		fontWeight: "bold",
		overflow: "hidden",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: 2,
	},

	[`& .${classes.thirdRow}`]: {
		display: "flex",
	},

	[`& .${classes.comment}`]: {
		color: black[100],
		fontSize: 12,
		overflow: "hidden",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: 2,
	},

	[`& .${classes.mentionTag}`]: {
		color: theme.palette.LinkDefault,
	},

	[`& .${classes.fourthRow}`]: {
		marginTop: 4,
		color: black[600],
		height: 16,
		display: "flex",
	},

	[`& .${classes.separator}`]: {
		margin: "0 3px",
	},
}));

export interface ChatCardProps {
	/** notification id */
	id: number;
}

// Two attributes on the third row share the same space (divided by "|" separator)
// If one attribute is small and occupy less then half - other attribute will be allowed to use all available space
// However, smaller attribute should always be fully shown, no truncation in favor of larger attribute
// This function create style min-width string which guarantee that smaller element will not be cut in favor of larger cmponent
const getMinWidth = (text?: string) => {
	text = text || "";
	const width = Math.ceil(html.getTextWidth(text) + 1);
	return `min(${width}px, 50% - 4px)`; // 4px offset is to leave space for separator between two components
};

export const getTaskDate = (taskNode: C8Node) => {
	return taskNode.AssignedDate || taskNode.StartDate || taskNode.$CT;
};

export const ChatCard: FunctionComponent<ChatCardProps> = (props) => {
	const { id } = props;
	const locale = useLocale();
	const notificationStore = useNotificationStore();
	const { cache } = notificationStore;
	const notification = notificationStore.getById(id);
	const commentNode = cache.get(notification.contextCNL);
	const c8Instance = cache.get(getCommentC8InstanceCnl(commentNode, cache));
	const isNew = isUnreadNotification(notification);

	// throttle server request to prevent frequent server request on each mouse click, prevent "ddos" like behavior.
	// double click is usually 500ms, so 1000 is long enough to cover possible multiple requests and short enough to prevent user inconvenience.
	const updateNotificationsThrottled = useRef(throttle((notificationId, isRead) => {
		updateNotifications([notificationId], isRead);
	}, 1000, { leading: false, trailing: true })).current;

	function onUnreadClick(e) {
		notificationStore.setRead(notification.id, isNew);
		updateNotificationsThrottled(notification.id, isNew);
	};

	function onUnreadClickPermissionless(e) {
		onUnreadClick(e);
		routerStore.setCenterFloatComponent(MessageDialog, {
			category: "warn",
			messageRes: "Location_ErrorNode",
			open: true,
			onClose: () => routerStore.setCenterFloatComponent(null),
		});
	};

	// if the c8 instance is not found, then the user does not have permission to view the full comment information
	if (!c8Instance || !commentNode) {
		// return a compact comment view
		const assignedDate = types.formatTime(notification.createdAt, types.FORMAT_DATETIME);
		const mentioned = locale.resource("NotificationMentionedYou", [locale.type(notification.contextType), ""]);
		const userText = locale.resource("User");
		return (
			<Root className={"csi-notification-card " + classes.chatCard + (isNew ? "  " + classes.chatCardNew : "")} onClick={onUnreadClickPermissionless}>
				{/* draw a circle with the blue variable color */}
				<div className={classes.image40} style={{ backgroundColor: blue[200], borderRadius: "50%" }} />
				<div className={classes.infoPane}>
					<div>
						<Text color="textPrimary" text={userText} />
						<span className={classes.dateAssigned}>{assignedDate}</span>
					</div>
					<div className={classes.secondRow}>
						<span className={classes.mentioned}>{mentioned}</span>
					</div>
					<ReadUnreadBtn isUnread={isNew} onClick={onUnreadClick} />
				</div>
			</Root>
		);
	}

	// Author of comment
	const { Author } = commentNode;
	const author = cache.get(Author) || xml.nodeCreateClientNode(Author, null, locale.resource("AccessViolation"));
	const authorName = author.$Name;

	// Comment text
	const commentText = html.htmlToText(commentNode.Comment);

	// Parse for current user tag (Example: "Text @UserName More Text"), 
	const currentUserUrl = sessionConstants.userUrl;
	const taggedUserName = "@" + cache.get(currentUserUrl).$Name; // get tagged user Name with
	const commentParts = commentText.split(new RegExp(taggedUserName, "i")); // get comment's pieces before and after tag

	// Slim card's first two attribute's text
	const specs = csiCard.getAttributeCardSlimSpecs(c8Instance.$Type);
	const ctx = new ContextConstructor(null, cache, c8Instance.$URL);
	const attrSpecs = specs.attributeSpecs;
	const attrNum = attrSpecs.length;
	const attr1 = (attrNum > 0) ? csiHeading.toText(ctx, specs.attributeSpecs[0].heading) : "";
	const attr2 = (attrNum > 1) ? csiHeading.toText(ctx, specs.attributeSpecs[1].heading) : "";

	// Date commented
	const assignedDate = types.formatTime(getTaskDate(commentNode), types.FORMAT_DATETIME);

	// Mentioned you label
	const mentioned = locale.resource("NotificationMentionedYou", [locale.type(c8Instance.$Type), c8Instance.$Name]);

	return (
		<Root className={"csi-notification-card " + classes.chatCard + (isNew ? "  " + classes.chatCardNew : "")}>
			<UserAvatar className={classes.image40} user={author} />
			<div className={classes.infoPane}>
				<div>
					<Text color="textPrimary" text={authorName} />
					<span className={classes.dateAssigned}>{assignedDate}</span>
				</div>
				<div className={classes.secondRow}>
					<span className={classes.mentioned}>{mentioned}</span>
				</div>
				<div className={classes.thirdRow}>
					<span className={classes.comment}>
						<span>{commentParts[0] || ""}</span>
						<span className={classes.mentionTag}>{taggedUserName}</span>
						<span>{commentParts[1] || ""}</span>
					</span>
				</div>
				<div className={classes.fourthRow}>
					<OverflowText style={{ minWidth: getMinWidth(attr1) }} text={attr1} />
					{attr2 ? <span className={classes.separator}>|</span> : ""}
					<OverflowText style={{ minWidth: getMinWidth(attr2) }} text={attr2} />
				</div>
				<ReadUnreadBtn isUnread={isNew} onClick={onUnreadClick} />
			</div>
		</Root>
	);
};